import * as React from "react";
import { Alert } from "react-bootstrap";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
    ISecret, IStateLoading, ILookupItem, AuthorizedProps
} from "../Model";
import { Server } from "../../server/Server";
import { apiConfig } from "../../msal/Config";
import WaitLoading from "../Common/WaitLoading";
import "./SecretManager.scss";
import { AppAlert } from '../../server/AppAlert';

const apiUrls = {
    getConfigValue: "api/SecretManager/getConfigValue",
    getSecretValue: "api/SecretManager/getSecretValue",
    setSecretValue: "api/SecretManager/setSecretValue",
    getSettingValue: "api/Lookup/getSettingValue",
    lookups: "api/ConfigEditor/lookups"
}

const hiddenSecret = "************";

interface IProp extends AuthorizedProps{
}

interface IState extends IStateLoading {
    urlSecrets: ISecret[];
    dbSecrets: ISecret[];
    confirmVisible: boolean;
    confirmAction: any;
    confirmTitle?: string;
    enabled: boolean;
    error?: string;
}

export default class SecretManager extends React.Component<IProp, IState> {

    constructor(props: any) {
        super(props);

        this.state = {
            loading: false,
            confirmVisible: false,
            confirmAction: null,
            urlSecrets: [],
            dbSecrets: [],
            enabled: false,
        }
    }

    public async componentDidMount() {
        this.loadSecrets();
    }

    public componentWillUnmount() {
    }

    public render() {
        if (this.state.error) {
            return (<Alert variant="danger">
                {this.state.error}
                   </Alert>);
        };
        return (
            <div className="secret-manager">
                <link rel="stylesheet" href="https://unpkg.com/@progress/kendo-font-icons@5/dist/index.css" />
                { this.state.loading && <WaitLoading />}
                { !this.state.enabled &&
                <Alert variant="warning">
                    Secret management is not enabled. You can enable this by setting 'SecretManagementEnabled' to 1 in settings.
                    <Button
                        title="Refresh status"
                        onClick={this.refreshStatus}
                        primary={true}
                        icon="refresh"
                        className="cmd refresh-status"
                        type="button"
                    >Refresh status</Button>
                </Alert>}
                <div className="url-secret-list">
                    <div className="section-header">
                        <h6> Url Connection passwords</h6>
                    </div>
                    <div className="section-body">
                        {this.state.urlSecrets.map((secret, index) => (
                            <div key={index} className="row">
                                <div className="col-1">
                                    <label htmlFor={secret.name} title={secret.hint}>{secret.title}</label>
                                </div>
                                <div className="col-3">
                                    <Input name={secret.name}
                                        type="text"
                                        id={secret.name}
                                        value={secret.value.length ? secret.value : hiddenSecret}
                                        onChange={this.changeTextValue_Url}
                                    />
                                    <Button
                                        title="View secret value"
                                        onClick={() => this.viewItem_Url(index)}
                                        icon="eye"
                                        className="cmd view-item"
                                        disabled={!this.state.enabled}
                                        type="button"
                                    > </Button>
                                </div>
                                <div className="col-1">
                                    <Button 
                                        title="Update secret value"
                                        onClick={() => this.updateItem_Url(index)}
                                        icon="save"
                                        className="cmd update-item"
                                        type="button"
                                        disabled={!this.state.enabled || !this.state.urlSecrets[index].value.length}
                                    > Update</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="db-secret-list">
                    <div className="section-header">
                        <h6> Database Connections</h6>
                    </div>
                    <div className="section-body">
                        {this.state.dbSecrets.map((secret, index) => (
                            <div key={index} className="row">
                                <div className="col-1">
                                    <label htmlFor={secret.name}>{secret.title}</label>
                                </div>
                                <div className="col-3">
                                    <Input name={secret.name}
                                        type="text"
                                        id={secret.name}
                                        value={secret.value.length ? secret.value : "******"}
                                        onChange={this.changeTextValue_DB}
                                    />
                                    <Button
                                        title="View secret value"
                                        onClick={() => this.viewItem_DB(index)}
                                        className="cmd view-item"
                                        icon="eye"
                                        type="button"
                                        disabled={!this.state.enabled}
                                    > </Button>
                                </div>
                                <div className="col-1">
                                    <Button
                                        title="Update secret value"
                                        onClick={() => this.updateItem_DB(index)}
                                        primary={true}
                                        icon="save"
                                        className="cmd update-item"
                                        type="button"
                                        disabled={!this.state.enabled || !this.state.dbSecrets[index].value.length}
                                    > Update</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {this.state.confirmVisible && (
                    <Dialog title={this.state.confirmTitle} onClose={() => this.toggleConfirm()}>
                        <p
                            style={{
                    margin: "25px",
                    textAlign: "center",
                }}
                        >
                            Are you sure you want to continue?
                        </p>
                        <DialogActionsBar>
                            <button className="k-button" onClick={() => this.toggleConfirm()}>
                                No
                            </button>
                            <button className="k-button" onClick={this.doConfirm}>
                                Yes
                            </button>
                        </DialogActionsBar>
                    </Dialog>
                )}
            </div>);
}

    private setPropertyValue(obj: any, property: string, newValue: any) {
        const props = property.split(".");
        let ds_prop: any = obj;
        for (let i = 0; i < props.length - 1; i++) {
            ds_prop = ds_prop[props[i]];
        }
        const oldValue = ds_prop[props[props.length - 1]];
        ds_prop[props[props.length - 1]] = newValue;
        return oldValue !== newValue;
    }

    private loadSecrets = async () => {
        try {
            this.setState({ loading: true });

            const enabled = await this.getSettingValue("SecretManagementEnabled");
            const lookups = await this.getSecrets();
            const sourceKeys = lookups["SourceKey"] as ILookupItem[];
            const dbNames = lookups["DBName"] as ILookupItem[];

            const dbSecrets = dbNames.map(item => { return { title: item.name, name: item.name, value: "" } as ISecret });

            const urlSecrets = sourceKeys.filter(item => JSON.parse(item.properties)?.secret !== 'None').map(item => {
                return { title: item.name, hint: `${item.name.toLowerCase()}User`, name: `${item.name.toLowerCase()}Pw`, value: "" } as ISecret
            });

            for (var i = 0; i < urlSecrets.length; i++) {
                try {
                    urlSecrets[i].hint = await this.getConfigValue(urlSecrets[i].hint);
                } catch (e) {
                    urlSecrets[i].hint = '';
                } 
            }

            this.setState({ enabled, dbSecrets, urlSecrets });
        }
        finally
        {
            this.setState({ loading: false });
        }
    }



    private refreshStatus = async () => {
        try {
            this.setState({ loading: true });

            const enabled = await this.getSettingValue("SecretManagementEnabled");

            this.setState({ enabled, loading:false });
        }
        catch(e) {
            this.setState({ loading: false });
            throw e;
        }
    }

    private getSecrets = async (): Promise<any> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        this.setState({
            loading: true
        });
        const lookups: any =
            await Server.getJson(apiUrls.lookups, apiAccessToken);
        return lookups;
    }

    private getSecretValue = async (secretName: string): Promise<string> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        const secret: ISecret =
            await Server.getJson(`${apiUrls.getSecretValue}?secretName=${secretName}`, apiAccessToken);
        return secret.value;
    }
        
    private getConfigValue = async (name: string): Promise<string> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        const secret: ISecret =
            await Server.getJson(`${apiUrls.getConfigValue}?configName=${name}`, apiAccessToken);
        return secret.value;
    }

    private getSettingValue = async (key: string): Promise<boolean> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        try {
            const secret: any =
                await Server.getJson(`${apiUrls.getSettingValue}?key=${key}`, apiAccessToken);
            return secret.value === "1";
        } catch (e) {
            if (e instanceof Error)
                this.setState({ error: e.message });
            else
                this.setState({ error: "Unknown error!" });
            return false;
        } 
    }

    private setSecretValue = async (secretName: string, secretValue: string): Promise<boolean> => {
        let apiAccessToken = await this.props.getAccessToken(apiConfig.resourceScope);
        return Server.postJson(`${apiUrls.setSecretValue}?secretName=${secretName}&secretValue=${encodeURIComponent(secretValue)}`, apiAccessToken)
            .then(() => {
                AppAlert.Success(`Secret name ${secretName} updated successfully.`);
                return true;
            })
            .catch((e) => {
                console.log("Exception updating secret value: " + e);
                return false;
            });
    }
        
    private toggleConfirm = (confirmTitle: string = "", confirmAction: any = null) => {
        this.setState({
            confirmAction,
            confirmTitle,
            confirmVisible: !this.state.confirmVisible
        });
    }

    private doConfirm = () => {
        if (this.state.confirmAction) {
            this.state.confirmAction();
            this.setState({
                confirmVisible: false
            });
        }
    }

    private viewItem_Url = async (index: number) => {
        if (!this.state.enabled) return;
        try {
            const urlSecrets = this.state.urlSecrets;
            let secret: ISecret = urlSecrets[index];
            if (secret.value.length) {
                secret.value = "";
            } else {
                this.setState({
                    loading: true
                });
                secret.value = await this.getSecretValue(secret.name);
            }
            this.setState({
                urlSecrets
            });
        }
        finally {
            this.setState({
                loading: false
            });
        }
    }

    private changeTextValue_Url = (event: any) => {
        const urlSecrets = this.state.urlSecrets;
        let secret: ISecret = urlSecrets.find(item => item.name === event.target.name)!;
        secret.value = event.target.value;
        this.setState({ urlSecrets});
    }

    private updateItem_Url = async (index: number) => {
        if (!this.state.enabled) return;
        try {
            const urlSecrets = this.state.urlSecrets;
            let secret: ISecret = urlSecrets[index];

            this.setState({
                loading: true
            });
            await this.setSecretValue(secret.name, secret.value);
            secret.value = "";
            this.setState({
                urlSecrets
            });
        }
        finally {
            this.setState({
                loading: false
            });
        }
    }

    private viewItem_DB = async (index: number) => {
        if (!this.state.enabled) return;
        try {
            const dbSecrets = this.state.dbSecrets;
            let secret: ISecret = dbSecrets[index];

            if (secret.value.length) {
                secret.value = "";
            } else {

                this.setState({
                    loading: true
                });
                secret.value = await this.getSecretValue(secret.name);
            }

            this.setState({
                dbSecrets
            });
        }
        finally {
            this.setState({
                loading: false
            });
        }
    }

    private changeTextValue_DB = (event: any) => {
        const dbSecrets = this.state.dbSecrets;
        let secret: ISecret = dbSecrets.find(item => item.name === event.target.name)!;
        secret.value = event.target.value;
        this.setState({ dbSecrets });
    }

    private updateItem_DB = async (index: number) => {
        if (!this.state.enabled) return;
        try {
            this.setState({
                loading: true
            });
            const dbSecrets = this.state.dbSecrets;
            let secret: ISecret = dbSecrets[index];
            await this.setSecretValue(secret.name, secret.value);
            secret.value = "";
            this.setState({
                dbSecrets
            });
        }
        finally {
            this.setState({
                loading: false
            });
        }
    }
}

